import React, { useState } from 'react';
import { format } from 'date-fns';
import { BodyText, Button, Spacer, Stack, Subtitle } from '@kvdbil/components';
import { useForm } from 'react-hook-form';
import {
  checkEligibility,
  EligibilityResult,
  MIN_VEHICLE_PRICE
} from './eligibility';
import { useTranslation } from '~/Locale';
import CheckBoxWithControl from '~/App/shared/components/Fields/CheckBoxWithControl';
import SelectWithControl from '~/App/shared/components/Fields/SelectWithControl';
import TextFieldWithControl from '~/App/shared/components/Fields/TextFieldWithControl';
import ElectricCarGrantResult from './ElectricCarGrantResult';
import {
  ElectricCarGrantFormContainer,
  ElectricCarGrantFormContent,
  ElectricCarGrantHelperText,
  NoWrapSpan,
  SubmitButtonWrapper
} from './styles';
import { ElectricCarGrantFormData } from './types';
import { SWEDISH_MUNICIPALITIES } from './constants';
import { formatMoneyWithoutCurrency } from '~/helpers/currency';
import { useSelector } from 'react-redux';
import { headerHeightSelector } from '~/App/shared/selectors/header';
import { scroller } from 'react-scroll';
import { useIsDevice } from '~/App/shared/hooks/useIsDevice';

const getHouseholdTypeOptions = (t: (key: string) => string) => [
  { value: 'single', label: t('Single adult') },
  { value: 'couple', label: t('Two adults') },
  { value: 'family', label: t('Family with children') }
];

const getOwnershipTypeOptions = (t: (key: string) => string) => [
  { value: 'purchase', label: t('Purchase') },
  { value: 'lease', label: t('Leasing') }
];

const getYesNoOptions = (t: (key: string) => string) => [
  { value: 'no', label: t('No') },
  { value: 'yes', label: t('Yes') }
];

const parseNumber = (v: string | number | undefined): number | undefined => {
  if (v === '' || v === undefined) return undefined;
  const num = Number(v);
  return isNaN(num) ? undefined : num;
};

const ElectricCarGrantForm = () => {
  const { t } = useTranslation();
  const headerHeight = useSelector(headerHeightSelector) ?? 0;
  const { isMobile } = useIsDevice();
  const [eligibilityResult, setEligibilityResult] =
    useState<EligibilityResult | null>(null);
  const householdTypeOptions = getHouseholdTypeOptions(t);
  const ownershipTypeOptions = getOwnershipTypeOptions(t);
  const yesNoOptions = getYesNoOptions(t);

  const { handleSubmit, control, watch } = useForm<ElectricCarGrantFormData>({
    defaultValues: {
      residenceDuration: true,
      householdType: householdTypeOptions[0],
      annualHouseholdIncome: undefined,
      applicationDate: format(new Date(), 'yyyy-MM-dd'),
      ownershipType: ownershipTypeOptions[0],
      stateIncomeTax: yesNoOptions[0],
      previousEvPhevOwnership: yesNoOptions[0],
      businessUse: yesNoOptions[0],
      previousOwnershipByHousehold: yesNoOptions[0],
      outstandingVehicleRelatedDebts: yesNoOptions[0],
      electricCarPurchasePrice: undefined,
      leasingFeePerMonth: undefined
    },
    mode: 'onBlur'
  });

  const onSubmit = (data: ElectricCarGrantFormData) => {
    const result = checkEligibility(data, t);
    setEligibilityResult(result);

    scroller.scrollTo('electric-car-grant-result', {
      duration: 500,
      delay: 100,
      smooth: true,
      isDynamic: true,
      offset: ((isMobile ? 0 : headerHeight) + 32) * -1
    });
  };

  const ownershipType = watch('ownershipType');
  const isPurchase = ownershipType?.value === 'purchase';
  const isLease = ownershipType?.value === 'lease';

  return (
    <ElectricCarGrantFormContainer>
      <Stack spacing={1} direction="column">
        <Subtitle as="h2" id="electric-car-grant-form-title">
          {t('Are you eligible for the electric car premium?')}
        </Subtitle>

        <BodyText>
          {t(
            'Fill in your details and check if you can get the electric car premium'
          )}
        </BodyText>
      </Stack>

      <Spacer height={1} />

      <ElectricCarGrantFormContent
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        aria-labelledby="electric-car-grant-form-title"
      >
        <Stack spacing={1.5} direction="column">
          <SelectWithControl
            control={control}
            name="municipality"
            label={t('Municipality where you are registered')}
            options={SWEDISH_MUNICIPALITIES}
            rules={{ required: t('Municipality is required') }}
            required
          />

          <CheckBoxWithControl
            control={control}
            name="residenceDuration"
            label={t(
              'Will you be registered here for at least four months after applying?'
            )}
            color="info"
          />

          <SelectWithControl
            control={control}
            name="householdType"
            label={t('What does the household look like?')}
            options={householdTypeOptions}
            rules={{ required: t('This field is required') }}
            required
          />

          <Stack spacing={0.25} direction="column">
            <TextFieldWithControl
              control={control}
              name="annualHouseholdIncome"
              type="number"
              label={t('Household combined annual income before tax')}
              suffix={<NoWrapSpan>{t('kr/år')}</NoWrapSpan>}
              rules={{
                required: t('This field is required'),
                min: {
                  value: 1,
                  message: t('Must be a positive number')
                },
                setValueAs: parseNumber
              }}
              required
            />
            <ElectricCarGrantHelperText>
              {t(
                'The electric car premium targets households with at most 80% of median income, calculated per household size. The exact income limit will be determined by Naturvårdsverket and updated regularly based on SCB statistics.'
              )}
            </ElectricCarGrantHelperText>
          </Stack>

          <Stack spacing={0.25} direction="column">
            <TextFieldWithControl
              control={control}
              name="applicationDate"
              type="date"
              label={t('Approximately when do you plan to apply?')}
              rules={{ required: t('This field is required') }}
              required
            />
            <ElectricCarGrantHelperText>
              {t(
                'The premium is higher for applications early in the period according to Naturvårdsverket proposal.'
              )}
            </ElectricCarGrantHelperText>
          </Stack>

          <SelectWithControl
            control={control}
            name="ownershipType"
            label={t('Ownership type')}
            options={ownershipTypeOptions}
            rules={{ required: t('This field is required') }}
            required
          />

          {isPurchase && (
            <TextFieldWithControl
              control={control}
              name="electricCarPurchasePrice"
              type="number"
              label={t('Purchase price of the electric car')}
              suffix={t('kr')}
              rules={{
                required: t('This field is required'),
                min: {
                  value: MIN_VEHICLE_PRICE,
                  message: t(
                    'Must be at least %s SEK',
                    formatMoneyWithoutCurrency(MIN_VEHICLE_PRICE)
                  )
                },
                setValueAs: parseNumber
              }}
              required
            />
          )}

          {isLease && (
            <TextFieldWithControl
              control={control}
              name="leasingFeePerMonth"
              type="number"
              label={t('Leasing fee per month')}
              suffix={t('kr/month')}
              rules={{
                required: t('This field is required'),
                min: {
                  value: 1,
                  message: t('Must be a positive number')
                },
                setValueAs: parseNumber
              }}
              required
            />
          )}

          <SelectWithControl
            control={control}
            name="stateIncomeTax"
            label={t('Does anyone in the household pay state income tax?')}
            options={yesNoOptions}
            rules={{ required: t('This field is required') }}
            required
          />

          <SelectWithControl
            control={control}
            name="previousEvPhevOwnership"
            label={t(
              'Has the household owned or leased an electric car or plug-in hybrid in the last 12 months?'
            )}
            options={yesNoOptions}
            rules={{ required: t('This field is required') }}
            required
          />

          <SelectWithControl
            control={control}
            name="businessUse"
            label={t(
              'Will the car be used in sole proprietorship or business operations?'
            )}
            options={yesNoOptions}
            rules={{ required: t('This field is required') }}
            required
          />

          <SelectWithControl
            control={control}
            name="previousOwnershipByHousehold"
            label={t(
              'Have you, your household or close relatives owned this car before?'
            )}
            options={yesNoOptions}
            rules={{ required: t('This field is required') }}
            required
          />

          <SelectWithControl
            control={control}
            name="outstandingVehicleRelatedDebts"
            label={t(
              'Do you have unpaid debts related to congestion tax or vehicle tax?'
            )}
            options={yesNoOptions}
            rules={{ required: t('This field is required') }}
            required
          />
        </Stack>

        <SubmitButtonWrapper>
          <Button
            type="submit"
            color="primary"
            colorShade="mainAlt"
            variant="solid"
            size="large"
            fullWidth
          >
            {t('Check if I can get the electric car premium')}
          </Button>
        </SubmitButtonWrapper>
      </ElectricCarGrantFormContent>

      {eligibilityResult && (
        <ElectricCarGrantResult result={eligibilityResult} />
      )}
    </ElectricCarGrantFormContainer>
  );
};

export default ElectricCarGrantForm;
